import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/integrations/supabase/client"
import { ITACONDO_PROFILE_FILTER } from "@/lib/itacondoScope"
import { SuperAdminStats } from "@/types/superadmin"

export const useSuperAdminStats = () => {
  return useQuery({
    queryKey: ["superadmin-stats"],
    queryFn: async (): Promise<SuperAdminStats> => {
      const now = new Date()
      const thirtyDaysAgo = new Date(now)
      thirtyDaysAgo.setDate(now.getDate() - 30)
      const thirtyDaysAhead = new Date(now)
      thirtyDaysAhead.setDate(now.getDate() + 30)

      const [activeUsers, newUsers, condominiums] = await Promise.all([
        supabase
          .from("profiles")
          .select("id", { count: "exact", head: true })
          .eq("is_active", true)
          .or(ITACONDO_PROFILE_FILTER),
        supabase
          .from("profiles")
          .select("id", { count: "exact", head: true })
          .gte("created_at", thirtyDaysAgo.toISOString())
          .or(ITACONDO_PROFILE_FILTER),
        supabase
          .from("condominiums")
          .select("payment_status, subscription_expires_at"),
      ])

      if (activeUsers.error) throw activeUsers.error
      if (newUsers.error) throw newUsers.error
      if (condominiums.error) throw condominiums.error

      const organizations = condominiums.data || []
      const expiringSoon = organizations.filter((organization) => {
        if (!organization.subscription_expires_at) return false
        const expiration = new Date(organization.subscription_expires_at)
        return expiration >= now && expiration <= thirtyDaysAhead
      }).length

      return {
        active_users: activeUsers.count || 0,
        total_condominiums: organizations.length,
        active_condominiums: organizations.filter((item) => item.payment_status === "active").length,
        overdue_condominiums: organizations.filter((item) => item.payment_status === "overdue").length,
        expiring_soon: expiringSoon,
        new_users_month: newUsers.count || 0,
        pending_payments: organizations.filter((item) => item.payment_status === "pending").length,
        suspended_condominiums: organizations.filter((item) => item.payment_status === "suspended").length,
      }
    },
    refetchInterval: 30000,
  })
}
